import { CustomerControllerApiFactory } from '@api-client/api'
import { Loading } from '@components/Loading'
import { QueryKey } from '@helpers/enums'
import { useAsyncApi } from '@hooks/useAsyncApi'
import { FormControlLabel } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { CheckboxWithValue } from '../CheckboxWithValue'
import { FormGroupWrapper, SectionContent, SectionHeader } from '../styles'

interface IProps {
	selectedCustomers: readonly string[]
	onCustomerListChange: (checked: boolean, value: string) => void
}

const CustomerListFilter: React.FC<IProps> = ({
	selectedCustomers,
	onCustomerListChange,
}: IProps) => {
	const { t } = useTranslation()
	const customerController = useAsyncApi(CustomerControllerApiFactory)

	const { data, isLoading, isError } = useQuery({
		queryKey: [QueryKey.GetCustomers],
		queryFn: () => customerController(api => api.listCustomers(0, 500)),
	})

	const handleCustomerListChange = (
		event: React.ChangeEvent<HTMLInputElement>,
		value: string
	) => {
		const { checked } = event.target
		onCustomerListChange(checked, value)
	}

	if (!data || isError) {
		return (
			<FormGroupWrapper>
				<SectionHeader component="div">
					{t('formField.customers')}
				</SectionHeader>
				<SectionContent>
					<p>{t('error')}</p>
				</SectionContent>
			</FormGroupWrapper>
		)
	}

	return (
		<FormGroupWrapper>
			<SectionHeader component="div">{t('formField.customers')}</SectionHeader>
			<SectionContent>
				{isLoading && (
					<div data-testid="customers-loading">
						<Loading />
					</div>
				)}
				{!isLoading &&
					data?.data.data.map((customer, i) => (
						<FormControlLabel
							key={i}
							control={
								<CheckboxWithValue
									value={customer.id}
									data-testid="customerlist-checkbox"
									checked={selectedCustomers.includes(customer.id)}
									onChange={handleCustomerListChange}
								/>
							}
							label={customer.name}
						/>
					))}
			</SectionContent>
		</FormGroupWrapper>
	)
}

export { CustomerListFilter }
